import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { InfraHistoryPoint } from '@/lib/types'

const CHART_HEIGHT = 220

const TOOLTIP_STYLE = {
  backgroundColor: 'var(--color-surface-0)',
  border: '1px solid var(--color-surface-3)',
  borderRadius: '8px',
  fontSize: '12px',
} as const

const AXIS_PROPS = {
  stroke: 'var(--color-text-tertiary)',
  fontSize: 11,
} as const

function EmptyChart({ label }: { label: string }): React.JSX.Element {
  return (
    <div className="flex h-[220px] items-center justify-center">
      <p className="text-sm text-text-tertiary">Waiting for {label} data...</p>
    </div>
  )
}

function ChartWrapper({
  title,
  sub,
  children,
}: {
  title: string
  sub: string
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
      <div className="mb-4">
        <h3 className="text-sm font-medium text-text-primary">{title}</h3>
        <p className="text-xs text-text-tertiary">{sub}</p>
      </div>
      {children}
    </div>
  )
}

export function CpuChart({ data }: { data: InfraHistoryPoint[] }): React.JSX.Element {
  return (
    <ChartWrapper title="CPU Usage" sub="Percentage over selected period">
      {data.length === 0 ? (
        <EmptyChart label="CPU" />
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-surface-3)" />
            <XAxis dataKey="time" {...AXIS_PROPS} />
            <YAxis domain={[0, 100]} {...AXIS_PROPS} tickFormatter={(v: number) => `${v}%`} />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(v: number) => [`${v.toFixed(1)}%`, 'CPU']}
            />
            <Area
              type="monotone"
              dataKey="cpu_percent"
              stroke="var(--color-admin-500)"
              fill="var(--color-admin-100)"
              strokeWidth={2}
              name="CPU"
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </ChartWrapper>
  )
}

export function MemoryChart({ data }: { data: InfraHistoryPoint[] }): React.JSX.Element {
  return (
    <ChartWrapper title="Memory Usage" sub="Percentage over selected period">
      {data.length === 0 ? (
        <EmptyChart label="memory" />
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-surface-3)" />
            <XAxis dataKey="time" {...AXIS_PROPS} />
            <YAxis domain={[0, 100]} {...AXIS_PROPS} tickFormatter={(v: number) => `${v}%`} />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(v: number) => [`${v.toFixed(1)}%`, 'Memory']}
            />
            <Area
              type="monotone"
              dataKey="memory_percent"
              stroke="var(--color-success)"
              fill="rgba(34,197,94,0.1)"
              strokeWidth={2}
              name="Memory"
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </ChartWrapper>
  )
}

export function DiskChart({ data }: { data: InfraHistoryPoint[] }): React.JSX.Element {
  return (
    <ChartWrapper title="Disk Usage" sub="Percentage over selected period">
      {data.length === 0 ? (
        <EmptyChart label="disk" />
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-surface-3)" />
            <XAxis dataKey="time" {...AXIS_PROPS} />
            <YAxis domain={[0, 100]} {...AXIS_PROPS} tickFormatter={(v: number) => `${v}%`} />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(v: number) => [`${v.toFixed(1)}%`, 'Disk']}
            />
            <Line
              type="monotone"
              dataKey="disk_percent"
              stroke="var(--color-warning)"
              strokeWidth={2}
              dot={false}
              name="Disk"
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </ChartWrapper>
  )
}

export function NetworkChart({ data }: { data: InfraHistoryPoint[] }): React.JSX.Element {
  return (
    <ChartWrapper title="Network I/O" sub="Mbps over selected period">
      {data.length === 0 ? (
        <EmptyChart label="network" />
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-surface-3)" />
            <XAxis dataKey="time" {...AXIS_PROPS} />
            <YAxis {...AXIS_PROPS} tickFormatter={(v: number) => `${v}`} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Line
              type="monotone"
              dataKey="network_rx_mbps"
              stroke="var(--color-admin-500)"
              strokeWidth={2}
              dot={false}
              name="RX Mbps"
            />
            <Line
              type="monotone"
              dataKey="network_tx_mbps"
              stroke="var(--color-danger)"
              strokeWidth={1.5}
              dot={false}
              name="TX Mbps"
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </ChartWrapper>
  )
}
